import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Beaker,
  Trophy,
  RotateCcw,
  TrendingUp,
  TrendingDown,
  Send,
  Eye,
  MousePointerClick,
  AlertTriangle,
} from "lucide-react";

export default function AdminEmailAB() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyTestId, setBusyTestId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email-ab");
      setTests(r.data.tests || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load A/B reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const declareWinner = async (testId, variant) => {
    if (!window.confirm(`Declare Variant ${variant} the winner? All future sends will use ${variant} only.`)) return;
    setBusyTestId(testId);
    try {
      const r = await api.post(`/admin/email-ab/${testId}/declare-winner`, { variant });
      setTests((prev) => prev.map((t) => (t.ab_test === testId ? r.data : t)));
      toast.success(`Variant ${variant} declared winner for ${testId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to declare winner");
    } finally {
      setBusyTestId(null);
    }
  };

  const resetTest = async (testId) => {
    if (!window.confirm("Reset to 50/50 random assignment? The declared winner will be cleared.")) return;
    setBusyTestId(testId);
    try {
      const r = await api.delete(`/admin/email-ab/${testId}/declare-winner`);
      setTests((prev) => prev.map((t) => (t.ab_test === testId ? r.data : t)));
      toast.success("Test re-opened to 50/50");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset");
    } finally {
      setBusyTestId(null);
    }
  };

  if (loading) {
    return (
      <div className="card p-12 flex items-center justify-center text-[var(--ink-2)]">
        <Loader2 size={20} className="animate-spin mr-3" /> Loading A/B reports…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-email-ab">
      {/* Hero */}
      <div className="card p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center flex-shrink-0">
            <Beaker size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-extrabold text-2xl tracking-tight text-[var(--ink)]">Email A/B Lab</h2>
            <p className="text-[var(--ink-2)] text-sm mt-1 leading-relaxed">
              Every transactional email is randomly assigned to Variant A (control) or Variant B (test). Resend webhooks
              report opens + clicks. When one variant beats the other with enough volume, declare it the winner — all
              future sends will use that variant only.
            </p>
          </div>
          <button
            className="btn-outline text-sm flex items-center gap-2"
            onClick={refresh}
            data-testid="ab-refresh"
          >
            <RotateCcw size={14} /> Refresh
          </button>
        </div>
      </div>

      {tests.length === 0 && (
        <div className="card p-12 text-center text-[var(--ink-2)]">
          No A/B tests are currently registered.
        </div>
      )}

      {tests.map((t) => (
        <ABTestCard
          key={t.ab_test}
          test={t}
          busy={busyTestId === t.ab_test}
          onDeclare={(v) => declareWinner(t.ab_test, v)}
          onReset={() => resetTest(t.ab_test)}
        />
      ))}
    </div>
  );
}

function ABTestCard({ test, busy, onDeclare, onReset }) {
  const variants = test.variants || [];
  const byV = Object.fromEntries(variants.map((v) => [v.variant, v]));
  const a = byV.A;
  const b = byV.B;
  const lift = test.lift_b_over_a;
  const winner = test.winner;
  const totalSent = variants.reduce((s, v) => s + v.sent, 0);

  return (
    <div className="card p-6" data-testid={`ab-test-${test.ab_test}`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-lg text-[var(--ink)]">{test.label}</h3>
            <span className="badge" style={{ background: "#F1F5F9", color: "#64748B" }}>
              {test.ab_test}
            </span>
            {winner && (
              <span className="badge flex items-center gap-1" style={{ background: "rgba(16,185,129,0.15)", color: "var(--primary-dark)" }}>
                <Trophy size={11} /> Winner: Variant {winner.variant}
              </span>
            )}
          </div>
          <p className="text-[var(--ink-2)] text-sm mt-1 leading-relaxed">{test.description}</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold">Total sent</div>
          <div className="font-display font-bold text-2xl text-[var(--ink)]">{totalSent.toLocaleString()}</div>
        </div>
      </div>

      {/* Variants grid */}
      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <VariantBlock
          variant="A"
          data={a}
          isWinner={winner?.variant === "A"}
          isLosing={lift && lift.open_rate_pp > 0 && !winner}
          onDeclare={() => onDeclare("A")}
          busy={busy}
        />
        <VariantBlock
          variant="B"
          data={b}
          isWinner={winner?.variant === "B"}
          isLosing={lift && lift.open_rate_pp < 0 && !winner}
          onDeclare={() => onDeclare("B")}
          busy={busy}
        />
      </div>

      {/* Lift summary */}
      {lift && (
        <div className="mt-5 p-4 rounded-lg bg-[var(--bg)] border border-[var(--line)]">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] font-semibold mb-3">B vs. A lift</div>
          <div className="grid grid-cols-2 gap-4">
            <LiftStat
              label="Open rate"
              pp={lift.open_rate_pp}
              relPct={lift.open_rate_rel_pct}
            />
            <LiftStat
              label="Click rate"
              pp={lift.click_rate_pp}
              relPct={lift.click_rate_rel_pct}
            />
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="mt-5 pt-4 border-t border-[var(--line)] flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-[var(--ink-3)] flex items-center gap-2">
          {totalSent < 50 && !winner && (
            <span className="inline-flex items-center gap-1 text-[var(--ink-2)]">
              <AlertTriangle size={12} className="text-amber-500" />
              Low volume — wait for ≥50 sends per variant before declaring a winner.
            </span>
          )}
          {winner && (
            <span className="text-[var(--ink-2)]">
              Declared by <strong className="text-[var(--ink)]">{winner.declared_by}</strong> ·{" "}
              {new Date(winner.declared_at).toLocaleString()}
            </span>
          )}
        </div>
        {winner ? (
          <button
            className="btn-outline text-sm flex items-center gap-2"
            onClick={onReset}
            disabled={busy}
            data-testid={`ab-reset-${test.ab_test}`}
          >
            <RotateCcw size={14} /> Re-open to 50/50
          </button>
        ) : null}
      </div>
    </div>
  );
}

function VariantBlock({ variant, data, isWinner, isLosing, onDeclare, busy }) {
  if (!data) {
    return (
      <div className="p-4 rounded-lg border border-dashed border-[var(--line)] bg-[var(--bg)] text-center text-[var(--ink-3)] text-sm">
        Variant {variant} — no sends yet
      </div>
    );
  }
  return (
    <div
      className={
        "p-4 rounded-lg border " +
        (isWinner
          ? "bg-[var(--primary)]/8 border-[var(--primary)]/40"
          : isLosing
          ? "bg-white border-[var(--line)] opacity-70"
          : "bg-white border-[var(--line)]")
      }
      data-testid={`ab-variant-${variant.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="badge font-bold" style={{ background: isWinner ? "var(--primary)" : "#0B132B", color: "#fff" }}>
            Variant {variant}
          </span>
          {isWinner && <Trophy size={14} className="text-[var(--primary-dark)]" />}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold">Sent</div>
          <div className="font-display font-bold text-base text-[var(--ink)]">{data.sent.toLocaleString()}</div>
        </div>
      </div>

      <div className="space-y-3">
        <RateBar
          icon={Eye}
          label="Open rate"
          rate={data.open_rate}
          numerator={data.unique_opened}
          denominator={data.sent}
          color="var(--ink)"
        />
        <RateBar
          icon={MousePointerClick}
          label="Click rate"
          rate={data.click_rate}
          numerator={data.unique_clicked}
          denominator={data.sent}
          color="var(--primary)"
        />
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-[var(--line)]">
        <Pill icon={Send} label="Delivered" value={data.delivered} />
        <Pill icon={AlertTriangle} label="Bounced" value={data.bounced} tone={data.bounced > 0 ? "danger" : "muted"} />
        <Pill icon={AlertTriangle} label="Complaints" value={data.complained} tone={data.complained > 0 ? "danger" : "muted"} />
      </div>

      {!isWinner && (
        <button
          className="btn-outline text-xs w-full mt-4 flex items-center justify-center gap-2"
          onClick={onDeclare}
          disabled={busy}
          data-testid={`ab-declare-${variant.toLowerCase()}`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Trophy size={12} />}
          Declare Variant {variant} the winner
        </button>
      )}
    </div>
  );
}

function RateBar({ icon: Icon, label, rate, numerator, denominator, color }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-xs text-[var(--ink-2)] font-semibold">
          <Icon size={12} /> {label}
        </div>
        <div className="text-sm font-display font-bold text-[var(--ink)]">
          {rate.toFixed(1)}% <span className="text-xs text-[var(--ink-3)] font-medium">({numerator}/{denominator})</span>
        </div>
      </div>
      <div className="w-full h-1.5 rounded-full bg-[var(--bg)] overflow-hidden border border-[var(--line)]">
        <div
          className="h-full transition-all"
          style={{ width: `${Math.min(100, rate)}%`, background: color }}
        />
      </div>
    </div>
  );
}

function Pill({ icon: Icon, label, value, tone = "muted" }) {
  const palette =
    tone === "danger" && value > 0
      ? { bg: "#FEE2E2", color: "#B91C1C" }
      : { bg: "#F1F5F9", color: "#475569" };
  return (
    <div className="text-center p-2 rounded-md" style={{ background: palette.bg, color: palette.color }}>
      <div className="text-[9px] uppercase tracking-widest font-semibold flex items-center justify-center gap-1">
        <Icon size={9} /> {label}
      </div>
      <div className="font-display font-bold text-sm">{value}</div>
    </div>
  );
}

function LiftStat({ label, pp, relPct }) {
  const up = pp > 0;
  const flat = pp === 0;
  const Icon = flat ? null : up ? TrendingUp : TrendingDown;
  const tone = flat ? "muted" : up ? "primary" : "danger";
  const color =
    tone === "primary" ? "var(--primary-dark)" : tone === "danger" ? "#B91C1C" : "#64748B";
  return (
    <div>
      <div className="text-xs text-[var(--ink-2)] font-semibold mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        {Icon && <Icon size={14} style={{ color }} />}
        <span className="font-display font-bold text-xl" style={{ color }}>
          {pp > 0 ? "+" : ""}
          {pp}pp
        </span>
        {relPct !== null && relPct !== undefined && (
          <span className="text-xs text-[var(--ink-3)]">
            ({relPct > 0 ? "+" : ""}
            {relPct}% rel)
          </span>
        )}
      </div>
    </div>
  );
}
